import { DownOutlined, TeamOutlined, UpOutlined } from '@ant-design/icons';
import { Avatar, Badge, Row, Space, Typography } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import MdsiIcon from 'assets/mdsi-icon.svg';
import { useTheme } from 'context/ThemeContext';
import { Business } from 'models/Business';
import { LegacyRef, memo, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { animated, config, useSpring } from 'react-spring';
import { useMeasure } from 'react-use';
import { useAppSelector } from 'redux/store';
import { InlineStylesModel } from '../../models/InlineStylesModel';
import { DivisionList } from './DivisionListItem';

// Define styles as a function that takes isDarkMode as a parameter
const getStyles = (isDarkMode: boolean): InlineStylesModel => ({
  wrapper: {
    // backgroundColor: isDarkMode ? 'rgba(57, 56, 60, 0.51)' : 'rgb(216, 217, 220)',
    padding: '16px',
    borderRadius: 8,
    marginBottom: 12,
    transition: 'all 0.2s ease',
    border: isDarkMode ? '1px solid transparent' : '1px solid transparent',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    backgroundColor: isDarkMode ? 'rgba(57, 56, 60, 0.51)' : 'rgba(255, 255, 255, 0.29)'
  },
  wrapperHover: {
    border: isDarkMode ? '1px solid rgb(69, 68, 76)' : '1px solid rgb(203, 203, 205)',
    backgroundColor: isDarkMode ? 'rgba(57, 56, 60, 0.51)' : 'rgba(255, 255, 255, 0.29)',

    padding: '16px',
    borderRadius: 8,
    marginBottom: 12,
    transition: 'all 0.2s ease',
    // boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    cursor: 'pointer'
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%'
  },
  infoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 16
  },
  title: {
    fontWeight: 500,
    marginBottom: 0,
    fontSize: 16,
    color: isDarkMode ? '#fff' : '#000'
  },
  mobileTitle: {
    fontWeight: 500,
    marginBottom: 0,
    fontSize: 14,
    color: isDarkMode ? '#fff' : '#000'
  },
  altTitle: {
    fontWeight: 400,
    color: '#18a799',
    marginBottom: 0
  },
  divider: {
    color: '#d9d9d9',
    margin: '0 8px'
  },
  badge: {
    backgroundColor: '#5867dd',
    color: '#1890ff',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 500,
    marginRight: 8,
    padding: '5px 12px',
    display: 'flex',
    alignItems: 'center'
  },
  iconBadge: {
    backgroundColor: '#5867dd',
    color: '#fff',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 500,
    marginRight: 8,
    padding: '5px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  mobileIconBadge: {
    backgroundColor: '#5867dd',
    color: '#fff',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 500,
    marginRight: 4,
    padding: '4px 8px',
    display: 'flex',
    alignItems: 'center',
    gap: '2px'
  },
  expandButton: {
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 12px',
    border: '1px solid #d9d9d9',
    transition: 'all 0.2s ease'
  },
  expandButtonActive: {
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 12px',
    border: '1px solid #1890ff',
    color: '#1890ff',
    backgroundColor: '#e6f7ff',
    transition: 'all 0.2s ease'
  },
  avatar: {
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    backgroundColor: 'white',
    padding: 2
  }
});

interface BusinessListItemProps {
  business: Business;
  onCustomerSelect?: () => void;
}

export const BusinessListItem = memo(({ business, onCustomerSelect }: BusinessListItemProps): JSX.Element => {
  /* ******************** Hooks ******************** */
  const { navSearchValue } = useAppSelector((state) => state.businessList);
  const { isDarkMode } = useTheme();
  const [showDivision, _setShowDivisions] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { pathname } = useLocation();
  const [ref, { height }] = useMeasure();
  const { xs, sm } = useBreakpoint();
  const isMobile = xs;

  // Get styles based on current theme
  const styles = useMemo(() => getStyles(isDarkMode), [isDarkMode]);

  const expandAnimation = useSpring({
    config: {
      ...config.gentle,
      clamp: true,
      // Add tension and friction to make animation more performant
      tension: 170,
      friction: 26
    },
    immediate: !!navSearchValue, // Skip animation if search is active
    height: showDivision ? height : 0,
    opacity: showDivision ? 1 : 0,
    transform: 'none' // Remove transform animation to improve performance
  });

  const isRecycleBinPage = useMemo(() => pathname.includes('recycling-bin'), [pathname]);
  const hasDivisions = useMemo(() => business.divisions.length > 0, [business.divisions.length]);

  /* ******************** Functions ******************** */
  const handleToggleShowDivision = useCallback(
    (ev: MouseEvent<HTMLElement>): void => {
      if (!hasDivisions) return;
      ev.preventDefault();
      ev.stopPropagation();
      requestAnimationFrame(() => {
        _setShowDivisions((prevState) => !prevState);
      });
    },
    [hasDivisions]
  );

  // Making the divisions expand when something is searched in the filter
  useEffect(() => {
    if (navSearchValue) {
      requestAnimationFrame(() => {
        _setShowDivisions(true);
      });
    }
    if (!hasDivisions) {
      _setShowDivisions(false);
    }
  }, [hasDivisions, navSearchValue]);

  useEffect(() => {
    if (navSearchValue === '') {
      requestAnimationFrame(() => {
        _setShowDivisions(false);
      });
    }
  }, [navSearchValue]);

  // Memoize the item style to prevent recalculation on each render
  const itemStyle = useMemo(() => {
    if (hasDivisions) {
      return isHovered ? styles.wrapperHover : styles.wrapper;
    }
    return styles.wrapper;
  }, [hasDivisions, isHovered, styles]);

  // Memoize the divisions text to prevent recalculation on each render
  const divisionsText = useMemo(() => (isRecycleBinPage ? `${business.divisions.length} Deleted Divisions` : `${business.divisions.length} Divisions`), [isRecycleBinPage, business.divisions.length]);

  // Memoize the avatar source to prevent recalculation on each render
  const avatarSrc = useMemo(() => business.logoUrl || MdsiIcon, [business.logoUrl]);

  // Render
  return (
    <>
      <div
        style={itemStyle}
        onClick={hasDivisions ? handleToggleShowDivision : undefined}
        onMouseEnter={() => hasDivisions && setIsHovered(true)}
        onMouseLeave={() => hasDivisions && setIsHovered(false)}>
        <div style={styles.container}>
          <div style={styles.infoContainer}>
            <Avatar src={avatarSrc} size={isMobile ? 32 : 48} style={styles.avatar} />
            <div>
              <Space>
                <Typography.Title level={5} style={isMobile ? styles.mobileTitle : styles.title}>
                  {business.name}
                </Typography.Title>
              </Space>

              {!isMobile && (
                <Row align="middle" style={{ marginTop: 4 }}>
                  <Typography.Text style={{ color: isDarkMode ? '#fff' : '#000' }} type="secondary">
                    {business.erpId}
                  </Typography.Text>
                </Row>
              )}

              {/* {isMobile && (
                <Typography.Text style={{ fontSize: '12px' }} type="secondary">
                  {business.erpId}
                </Typography.Text>
              )} */}
            </div>
          </div>

          {hasDivisions && (
            <>
              <Badge
                count={
                  <Space style={isMobile ? styles.mobileIconBadge : styles.iconBadge} align="center">
                    <TeamOutlined style={{ color: '#fff', fontSize: isMobile ? 12 : 14 }} />
                    {!isMobile && <Typography.Text style={{ color: '#fff', margin: 0 }}>{business.divisions.length}</Typography.Text>}
                    {showDivision ? <UpOutlined style={{ color: '#fff', fontSize: isMobile ? 10 : 14 }} /> : <DownOutlined style={{ color: '#fff', fontSize: isMobile ? 10 : 14 }} />}
                  </Space>
                }
                style={isMobile ? styles.mobileIconBadge : styles.iconBadge}
                showZero
              />
            </>
          )}
        </div>
      </div>

      <animated.div
        style={{
          overflow: 'hidden',
          marginBottom: showDivision ? 0 : 0,
          ...expandAnimation
        }}>
        {showDivision && (
          <div ref={ref as LegacyRef<HTMLDivElement>}>
            <DivisionList divisions={business.divisions} onCustomerSelect={onCustomerSelect} />
          </div>
        )}
      </animated.div>
    </>
  );
});
